import { Fn } from '@scansource-brazil/zeus-core-ui/types';
import { SESSION_STATUS_ENUM } from '~/enums/Session.enum';
import { isEmptyValue } from '@scansource-brazil/zeus-core-ui/data';

export class Session {
  currentEconomicGroupMember: string = '';
  token: string = '';
  refreshToken: string = '';
  name: string = '';
  lastName: string = '';
  userName: string = '';
  email: string = '';
  roles: string[] = [];
  groups: string[] = [];
  economicGroupMembers: string[] = [];
  authenticated: boolean = false;
  sessionStatus: SESSION_STATUS_ENUM = SESSION_STATUS_ENUM.DEFAULT;
  lastVisitTimestamp?: number;

  /**
   * Creates an instance of Session.
   * @param sessionData - Optional Keycloak instance to initialize the session.
   */
  constructor(sessionData?: TSessionData) {
    this.currentEconomicGroupMember = sessionData?.currentEconomicGroupMember ?? '';
    this.economicGroupMembers = sessionData?.economicGroupMembers ?? [];
    this.token = sessionData?.token ?? '';
    this.refreshToken = sessionData?.refreshToken ?? '';
    this.name = sessionData?.name ?? '';
    this.lastName = sessionData?.lastName ?? '';
    this.userName = sessionData?.userName ?? '';
    this.email = sessionData?.email ?? '';
    this.economicGroupMembers = sessionData?.economicGroupMembers ?? [];
    this.authenticated = sessionData?.authenticated ?? false;
    this.sessionStatus = sessionData?.sessionStatus ?? SESSION_STATUS_ENUM.DEFAULT;
    this.roles = sessionData?.roles ?? [];
    this.groups = sessionData?.groups ?? [];
    this.lastVisitTimestamp = sessionData?.lastVisitTimestamp;
  }

  get isItBack(): boolean {
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    if (!this.lastVisitTimestamp) return false;

    return Date.now() - this.lastVisitTimestamp >= fiveMinutes && !this.authenticated;
  }

  /**
   * Opens a session with the provided Keycloak data.
   * @param sessionData - The Keycloak instance containing session data.
   */
  openSession(sessionData: TSessionData): Session {
    this.currentEconomicGroupMember = sessionData.currentEconomicGroupMember;
    this.economicGroupMembers = sessionData.economicGroupMembers;
    this.token = sessionData.token;
    this.refreshToken = sessionData.refreshToken;
    this.name = sessionData.name;
    this.lastName = sessionData.lastName;
    this.userName = sessionData.userName;
    this.email = sessionData.email;
    this.economicGroupMembers = sessionData.economicGroupMembers;
    this.authenticated = sessionData.authenticated;
    this.sessionStatus = sessionData.sessionStatus;
    this.roles = sessionData.roles;
    this.groups = sessionData.groups;
    this.lastVisitTimestamp = undefined;
    return this;
  }

  /**
   * Refreshes the session with the provided Keycloak data.
   * @param sessionData - The Keycloak instance containing session data.
   * @param onRefreshCallback - Optional callback function to be called after refreshing the session.
   */
  refreshSession(sessionData: TSessionData, onRefreshCallback?: Fn): Session {
    const isValid = this.validate(sessionData);

    if (!isValid) return this.openSession(sessionData);

    this.token = sessionData.token;
    this.refreshToken = sessionData.refreshToken;
    this.authenticated = sessionData.authenticated;
    this.sessionStatus = sessionData.sessionStatus;
    this.roles = sessionData.roles;
    this.groups = sessionData.groups;
    this.name = sessionData.name;
    this.lastName = sessionData.lastName;

    if (onRefreshCallback) onRefreshCallback();
    return this;
  }

  /**
   * Sets the tenant ID.
   * @param economicGroupMemberId - The tenant ID to set.
   */
  setCurrentEconomicGroupMember(economicGroupMemberId: string): Session {
    this.currentEconomicGroupMember = economicGroupMemberId;
    return this;
  }

  /**
   * Closes the session and resets all session data.
   */
  closeSession(): void {
    Object.assign(
      this,
      new Session({
        sessionStatus: SESSION_STATUS_ENUM.DEFAULT,
      } as TSessionData),
    );
  }

  /**
   * Closes the session window and resets the last visit timestamp.
   * This method is used to handle the case when the user closes the browser window or tab.
   */
  private closeWindow(): void {
    this.sessionStatus = SESSION_STATUS_ENUM.WINDOW_CLOSED;
    this.lastVisitTimestamp = Date.now();
    this.authenticated = false;
  }

  /**
   * Interrupts the session and sets the session status.
   * @param status - The status to set for the session.
   */
  interruptSession(status: SESSION_STATUS_ENUM): Session {
    if (status === SESSION_STATUS_ENUM.LOGGED_OUT) this.closeSession();
    if (status === SESSION_STATUS_ENUM.WINDOW_CLOSED) this.closeWindow();
    this.sessionStatus = status;
    return this;
  }

  /**
   * Resets the session status to the default value.
   */
  resetSessionStatus(): Session {
    this.sessionStatus = SESSION_STATUS_ENUM.DEFAULT;
    return this;
  }

  hasGroup(group: string): boolean {
    return this.groups.some((g) => g === group);
  }

  hasRole(role: string): boolean {
    return this.roles.some((r) => r === role);
  }

  /**
   * Validates whether the provided session data matches the current session.
   *
   * Verification checks include:
   * - Session data exists and has no empty values
   * - Groups match
   * - Email matches
   * - Username matches
   * - Session status is default
   * - Economic group members are the same
   *
   * @param sessionData - The session data to validate against the current session
   * @returns Boolean indicating if the session data is valid (true) or invalid (false)
   * @   */
  validate(sessionData: TSessionData): boolean {
    const isValid = false;
    if (!sessionData) return isValid;
    if (Object.values(sessionData).some((value) => isEmptyValue(value))) return isValid;

    if (sessionData.groups !== this.groups) return isValid;
    if (sessionData.email !== this.email) return isValid;
    if (sessionData.userName !== this.userName) return isValid;
    if (sessionData.sessionStatus !== SESSION_STATUS_ENUM.DEFAULT) return isValid;

    const hasSameEconomicGroupMembers = sessionData.economicGroupMembers.every((member) =>
      this.economicGroupMembers.includes(member),
    );

    if (!hasSameEconomicGroupMembers) return isValid;

    return true;
  }
}

export type TSessionData = Pick<
  Session,
  | 'token'
  | 'refreshToken'
  | 'authenticated'
  | 'sessionStatus'
  | 'economicGroupMembers'
  | 'currentEconomicGroupMember'
  | 'name'
  | 'lastName'
  | 'userName'
  | 'email'
  | 'roles'
  | 'groups'
  | 'lastVisitTimestamp'
>;
